import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { money, clock } from "@/lib/format";

import {
  PageHeader,
  Loader,
  EmptyState,
} from "@/components/common/Bits";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import {
  Users,
  Phone,
  Mail,
  Eye,
  Search,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  CalendarDays,
} from "lucide-react";

const PAGE_SIZE = 20;

const getLocalDate = () => {
  const now = new Date();

  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export default function Customers() {
  const [customers, setCustomers] = useState([]);

  const [detail, setDetail] = useState(null);

  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  // ---------------------------------------------------------
  // Filters
  // ---------------------------------------------------------

  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedDate, setSelectedDate] =
    useState(getLocalDate);

  // ---------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------

  const [page, setPage] = useState(1);

  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    page_size: PAGE_SIZE,
    total_pages: 1,
  });

  // ---------------------------------------------------------
  // Load Customers
  // ---------------------------------------------------------

  const loadCustomers = async () => {
    setLoading(true);

    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        date: selectedDate,
      };

      // Takeaway / Delivery filter
      if (filter !== "all") {
        params.type = filter;
      }

      // Search
      if (search.trim()) {
        params.search = search.trim();
      }

      const r = await api.customers(params);

      const data = r.data || {};

      /*
       * Expected backend response:
       *
       * {
       *   items: [],
       *   total: 100,
       *   page: 1,
       *   page_size: 20,
       *   total_pages: 5
       * }
       */

      // Temporary compatibility with old backend response
      if (Array.isArray(data)) {
        setCustomers(data);

        setPagination({
          total: data.length,
          page: 1,
          page_size: PAGE_SIZE,
          total_pages: Math.max(
            1,
            Math.ceil(data.length / PAGE_SIZE)
          ),
        });

        return;
      }

      setCustomers(data.items || []);

      setPagination({
        total: data.total || 0,
        page: data.page || page,
        page_size: data.page_size || PAGE_SIZE,
        total_pages: data.total_pages || 1,
      });
    } catch (e) {
      console.error("Failed to load customers:", e);

      setCustomers([]);

      setPagination({
        total: 0,
        page: 1,
        page_size: PAGE_SIZE,
        total_pages: 1,
      });
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------
  // Reload when filters / page change
  // ---------------------------------------------------------

  useEffect(() => {
    loadCustomers();
  }, [page, filter, search, selectedDate]);

  // ---------------------------------------------------------
  // Filter change
  // ---------------------------------------------------------

  const handleFilterChange = (value) => {
    setFilter(value);
    setPage(1);
  };

  // ---------------------------------------------------------
  // Search change
  // ---------------------------------------------------------

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  // ---------------------------------------------------------
  // Date change
  // ---------------------------------------------------------

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setPage(1);
  };

  // ---------------------------------------------------------
  // Reset
  // ---------------------------------------------------------

  const handleReset = () => {
    setFilter("all");
    setSearch("");
    setSelectedDate(getLocalDate());
    setPage(1);
  };

  // ---------------------------------------------------------
  // Customer Detail
  // ---------------------------------------------------------

  const open = async (customer) => {
    setDetail({
      customer,
      orders: null,
    });

    setDetailLoading(true);

    try {
      const r = await api.customer(customer.id);

      setDetail(r.data);
    } catch (e) {
      console.error("Failed to load customer:", e);

      setDetail({
        customer,
        orders: [],
      });
    } finally {
      setDetailLoading(false);
    }
  };

  // ---------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------

  const totalPages = Math.max(
    1,
    pagination.total_pages || 1
  );

  const start =
    pagination.total === 0
      ? 0
      : (page - 1) * PAGE_SIZE + 1;

  const end =
    pagination.total === 0
      ? 0
      : Math.min(
          page * PAGE_SIZE,
          pagination.total
        );

  const getPages = () => {
    const pages = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }

      return pages;
    }

    pages.push(1);

    if (page > 4) {
      pages.push("...");
    }

    const startPage = Math.max(2, page - 1);

    const endPage = Math.min(
      totalPages - 1,
      page + 1
    );

    for (
      let i = startPage;
      i <= endPage;
      i++
    ) {
      pages.push(i);
    }

    if (page < totalPages - 3) {
      pages.push("...");
    }

    pages.push(totalPages);

    return pages;
  };

  // ---------------------------------------------------------
  // Render
  // ---------------------------------------------------------

  return (
    <div className="p-4 sm:p-6 animate-fade-up">

      <PageHeader
        title="Customers"
        subtitle={`${pagination.total} customers`}
      />

      {/* =====================================================
          FILTER BAR
      ====================================================== */}

      <div className="mt-5 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">

        {/* LEFT SIDE */}
        <div className="flex flex-col sm:flex-row gap-3">

          {/* Search */}
          <div className="relative w-full sm:w-[280px]">
            <Search
              size={17}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />

            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Search customer or phone..."
              className="h-10 w-full rounded-xl border border-border bg-white pl-10 pr-4 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10"
            />
          </div>

          {/* Order Type Filters */}
          <div className="flex items-center gap-2 overflow-x-auto">

            <FilterButton
              active={filter === "all"}
              onClick={() =>
                handleFilterChange("all")
              }
            >
              All
            </FilterButton>

            <FilterButton
              active={filter === "takeaway"}
              onClick={() =>
                handleFilterChange("takeaway")
              }
            >
              Takeaway
            </FilterButton>

            <FilterButton
              active={filter === "delivery"}
              onClick={() =>
                handleFilterChange("delivery")
              }
            >
              Delivery
            </FilterButton>

          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="flex items-center gap-2">

          {/* Date */}
          <div className="relative flex items-center">

            <CalendarDays
              size={16}
              className="absolute left-3 text-primary pointer-events-none"
            />

            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              className="h-10 rounded-xl border border-border bg-white pl-9 pr-3 text-sm font-medium outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10"
            />

          </div>

          {/* Reset */}
          <button
            type="button"
            onClick={handleReset}
            className="h-10 inline-flex items-center gap-2 rounded-xl border border-border bg-white px-4 text-sm font-semibold text-foreground transition hover:bg-secondary"
          >
            <RotateCcw size={15} />
            <span className="hidden sm:inline">
              Reset
            </span>
          </button>

        </div>
      </div>

      {/* =====================================================
          TABLE
      ====================================================== */}

      <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-white">

        {loading ? (
          <div className="p-8">
            <Loader label="Loading customers" />
          </div>
        ) : customers.length === 0 ? (
          <div className="p-8">
            <EmptyState
              icon={Users}
              title="No customers found"
              subtitle={
                search
                  ? "Try a different customer name or phone number."
                  : "No customers found for the selected filters and date."
              }
            />
          </div>
        ) : (
          <>
            {/* =================================================
                TABLE
            ================================================== */}

            <div className="overflow-x-auto">

              <table className="w-full min-w-[900px] text-sm">

                <thead>
                  <tr className="border-b border-border bg-secondary/30">

                    <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                      Customer
                    </th>

                    <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                      Phone
                    </th>

                    <th className="px-5 py-3 text-center text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                      Orders
                    </th>

                    <th className="px-5 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                      Spent
                    </th>

                    <th className="px-5 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                      AOV
                    </th>

                    <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                      Last Order
                    </th>

                    <th className="px-5 py-3 text-center text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                      Action
                    </th>

                  </tr>
                </thead>

                <tbody>

                  {customers.map((customer) => (

                    <tr
                      key={customer.id}
                      data-testid={`customer-${customer.id}`}
                      className="border-b border-border last:border-b-0 hover:bg-secondary/20 transition-colors"
                    >

                      {/* Customer */}
                      <td className="px-5 py-4">

                        <div className="flex items-center gap-3">

                          <span className="h-10 w-10 shrink-0 rounded-xl bg-primary/10 text-primary grid place-items-center font-display font-bold">
                            {customer.name
                              ?.charAt(0)
                              ?.toUpperCase() || "?"}
                          </span>

                          <div className="min-w-0">

                            <p className="font-semibold truncate max-w-[220px]">
                              {customer.name || "Unknown"}
                            </p>

                            {customer.email && (
                              <p className="text-xs text-muted-foreground truncate max-w-[220px]">
                                {customer.email}
                              </p>
                            )}

                          </div>

                        </div>

                      </td>

                      {/* Phone */}
                      <td className="px-5 py-4">

                        <div className="flex items-center gap-1.5 text-muted-foreground whitespace-nowrap">

                          <Phone size={13} />

                          <span>
                            {customer.phone || "—"}
                          </span>

                        </div>

                      </td>

                      {/* Orders */}
                      <td className="px-5 py-4 text-center">

                        <span className="inline-flex min-w-8 justify-center rounded-lg bg-secondary px-2 py-1 font-semibold">
                          {customer.total_orders ?? 0}
                        </span>

                      </td>

                      {/* Spent */}
                      <td className="px-5 py-4 text-right font-semibold whitespace-nowrap">
                        {money(
                          customer.total_spend || 0
                        )}
                      </td>

                      {/* AOV */}
                      <td className="px-5 py-4 text-right font-semibold whitespace-nowrap">
                        {money(
                          customer.avg_order_value || 0
                        )}
                      </td>

                      {/* Last Order */}
                      <td className="px-5 py-4 text-muted-foreground whitespace-nowrap">

                        {customer.last_order
                          ? clock(customer.last_order)
                          : "—"}

                      </td>

                      {/* Action */}
                      <td className="px-5 py-4 text-center">

                        <button
                          type="button"
                          onClick={() =>
                            open(customer)
                          }
                          aria-label={`View ${customer.name}`}
                          className="inline-grid h-8 w-8 place-items-center rounded-lg border border-border bg-white text-foreground transition hover:border-primary hover:bg-primary/5 hover:text-primary"
                        >
                          <Eye size={16} />
                        </button>

                      </td>

                    </tr>

                  ))}

                </tbody>

              </table>

            </div>

            {/* =================================================
                PAGINATION
            ================================================== */}

            {pagination.total > PAGE_SIZE && (

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-border bg-white px-4 py-3">

                {/* Result count */}
                <div className="text-xs text-muted-foreground whitespace-nowrap">

                  Showing{" "}

                  <span className="font-semibold text-foreground">
                    {start}
                  </span>

                  {" "}to{" "}

                  <span className="font-semibold text-foreground">
                    {end}
                  </span>

                  {" "}of{" "}

                  <span className="font-semibold text-foreground">
                    {pagination.total}
                  </span>

                </div>

                {/* Pages */}
                <div className="flex items-center justify-center gap-1">

                  {/* Previous */}
                  <button
                    type="button"
                    onClick={() =>
                      setPage(
                        Math.max(1, page - 1)
                      )
                    }
                    disabled={page === 1}
                    className="grid h-8 w-8 place-items-center rounded-lg border border-border bg-white text-foreground transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={16} />
                  </button>

                  {/* Page Numbers */}
                  {getPages().map(
                    (item, index) =>
                      item === "..." ? (
                        <span
                          key={`ellipsis-${index}`}
                          className="grid h-8 w-8 place-items-center text-xs text-muted-foreground"
                        >
                          ...
                        </span>
                      ) : (
                        <button
                          key={item}
                          type="button"
                          onClick={() =>
                            setPage(item)
                          }
                          className={
                            page === item
                              ? "grid h-8 min-w-8 place-items-center rounded-lg bg-primary px-2 text-xs font-semibold text-white"
                              : "grid h-8 min-w-8 place-items-center rounded-lg border border-border bg-white px-2 text-xs font-semibold text-foreground hover:bg-secondary"
                          }
                        >
                          {item}
                        </button>
                      )
                  )}

                  {/* Next */}
                  <button
                    type="button"
                    onClick={() =>
                      setPage(
                        Math.min(
                          totalPages,
                          page + 1
                        )
                      )
                    }
                    disabled={page === totalPages}
                    className="grid h-8 w-8 place-items-center rounded-lg border border-border bg-white text-foreground transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Next page"
                  >
                    <ChevronRight size={16} />
                  </button>

                </div>

              </div>

            )}

          </>
        )}

      </div>

      {/* =====================================================
          CUSTOMER DETAIL MODAL
          KEEPING EXISTING DESIGN
      ====================================================== */}

      <Dialog
        open={!!detail}
        onOpenChange={(v) =>
          !v && setDetail(null)
        }
      >

        <DialogContent
          className="bg-white sm:max-w-md"
          data-testid="customer-detail"
        >

          {detail && (
            <>

              <DialogHeader>

                <DialogTitle className="font-display">
                  {detail.customer.name}
                </DialogTitle>

              </DialogHeader>

              <div className="text-sm text-muted-foreground space-y-1">

                <p className="flex items-center gap-2">
                  <Phone size={13} />
                  {detail.customer.phone}
                </p>

                {detail.customer.email && (
                  <p className="flex items-center gap-2">
                    <Mail size={13} />
                    {detail.customer.email}
                  </p>
                )}

              </div>

              <div className="grid grid-cols-3 gap-2 text-center">

                <Stat
                  l="Orders"
                  v={
                    detail.customer
                      .total_orders
                  }
                />

                <Stat
                  l="Total Spent"
                  v={money(
                    detail.customer
                      .total_spend
                  )}
                />

                <Stat
                  l="Avg Order"
                  v={money(
                    detail.customer
                      .avg_order_value
                  )}
                />

              </div>

              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mt-2">
                Order history
              </p>

              <div className="max-h-64 overflow-y-auto space-y-1.5">

                {detailLoading ||
                !detail.orders ? (
                  <Loader label="Loading" />
                ) : detail.orders.length ===
                  0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    No orders
                  </p>
                ) : (
                  detail.orders.map((o) => (

                    <div
                      key={o.id}
                      className="flex items-center justify-between gap-2 rounded-xl border border-border px-3 py-2 text-sm"
                    >

                      <span className="font-semibold">
                        #{o.order_no}
                      </span>

                      <span className="text-muted-foreground capitalize">
                        {String(
                          o.type || ""
                        ).replace(
                          "_",
                          " "
                        )}
                      </span>

                      <span className="text-muted-foreground">
                        {clock(
                          o.created_at
                        )}
                      </span>

                      <span className="font-bold">
                        {money(o.paid_amount)}
                      </span>

                    </div>

                  ))
                )}

              </div>

            </>
          )}

        </DialogContent>

      </Dialog>

    </div>
  );
}

// ===========================================================
// Filter Button
// ===========================================================

const FilterButton = ({
  active,
  onClick,
  children,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={
      active
        ? "h-10 rounded-xl bg-primary px-4 text-sm font-semibold text-white whitespace-nowrap"
        : "h-10 rounded-xl border border-border bg-white px-4 text-sm font-semibold text-foreground whitespace-nowrap transition hover:bg-secondary"
    }
  >
    {children}
  </button>
);

// ===========================================================
// Stat
// ===========================================================

const Stat = ({ l, v }) => (
  <div className="rounded-xl bg-secondary/50 py-2">

    <p className="font-display font-bold text-sm">
      {v}
    </p>

    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
      {l}
    </p>

  </div>
);